import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import Modal from "../../../components/Modal";
import { entregaService, Entrega } from "../../../services/entregaService";
import { BrazilDate } from "../../../utils/BrazilDate";

interface ModalBuscarPedidoProps {
  isOpen: boolean;
  onClose: () => void;
  lojaId: number | undefined;
}

function statusLabel(e: Entrega): string {
  if (e.status === "ativo" && !e.dataFim) return "Em rota";
  if (e.dataFim) return "Finalizada";
  return e.status;
}

const ModalBuscarPedido: React.FC<ModalBuscarPedidoProps> = ({
  isOpen,
  onClose,
  lojaId,
}) => {
  const [numero, setNumero] = useState("");
  const [periodo, setPeriodo] = useState<"hoje" | "7d">("hoje");
  const [resultados, setResultados] = useState<Entrega[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [jaBuscou, setJaBuscou] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setNumero("");
      setResultados([]);
      setErro(null);
      setPeriodo("hoje");
      setJaBuscou(false);
    }
  }, [isOpen]);

  const buscar = async () => {
    setErro(null);
    const n = parseInt(numero.replace(/\D/g, "").trim(), 10);
    if (!Number.isFinite(n) || n < 1) {
      setErro("Informe um número de pedido válido.");
      return;
    }
    if (!lojaId) {
      setErro("Selecione uma loja para buscar.");
      return;
    }

    const fim = BrazilDate.operationalTodayISO();
    const inicio =
      periodo === "hoje" ? fim : BrazilDate.subtractCalendarDaysFromISO(fim, 7);

    setBuscando(true);
    setJaBuscou(true);
    try {
      const res = await entregaService.getAll({
        lojaId,
        pedidoDisplayId: n,
        dataInicio: inicio,
        dataFim: fim,
        limit: 30,
        page: 1,
      });
      setResultados(res.data);
    } catch (e) {
      setErro(
        e instanceof Error ? e.message : "Não foi possível buscar agora."
      );
      setResultados([]);
    } finally {
      setBuscando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Buscar pedido na entrega">
      <div className="space-y-4">
        <p className="text-sm text-neutral-600">
          Informe o número do pedido (display) para ver qual entregador levou e
          quando a saída foi registrada.
        </p>

        {!lojaId && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Selecione uma loja no topo da página para usar a busca.
          </p>
        )}

        <div>
          <label
            htmlFor="busca-pedido-numero"
            className="mb-1 block text-sm font-medium text-neutral-700"
          >
            Número do pedido
          </label>
          <input
            id="busca-pedido-numero"
            type="text"
            inputMode="numeric"
            value={numero}
            onChange={(e) => setNumero(e.target.value.replace(/\D/g, ""))}
            placeholder="Ex: 42"
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-neutral-900 placeholder-neutral-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-neutral-700">
            Período
          </legend>
          <div className="flex flex-wrap gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="periodo-busca-pedido"
                checked={periodo === "hoje"}
                onChange={() => setPeriodo("hoje")}
              />
              Hoje
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="periodo-busca-pedido"
                checked={periodo === "7d"}
                onChange={() => setPeriodo("7d")}
              />
              Últimos 7 dias
            </label>
          </div>
        </fieldset>

        <button
          type="button"
          onClick={buscar}
          disabled={buscando || !lojaId}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 font-semibold text-white shadow transition hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-neutral-400"
        >
          <Search size={18} />
          {buscando ? "Buscando…" : "Buscar"}
        </button>

        {erro && (
          <p className="text-sm text-red-600" role="alert">
            {erro}
          </p>
        )}

        {resultados.length > 0 && (
          <div className="max-h-72 space-y-3 overflow-y-auto rounded-lg border border-neutral-200 bg-neutral-50 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              {resultados.length}{" "}
              {resultados.length === 1 ? "resultado" : "resultados"}
            </p>
            <ul className="space-y-3">
              {resultados.map((e) => (
                <li
                  key={e.id}
                  className="rounded-md border border-neutral-200 bg-white p-3 text-sm shadow-sm"
                >
                  <div className="font-semibold text-neutral-900">
                    {e.entregador.nome}
                  </div>
                  <dl className="mt-2 space-y-1 text-neutral-700">
                    <div className="flex justify-between gap-2">
                      <dt className="text-neutral-500">Saída (início)</dt>
                      <dd className="text-right tabular-nums">
                        {BrazilDate.formatBRNaive(e.dataInicio, true)}
                      </dd>
                    </div>
                    {e.dataFim && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-neutral-500">Retorno / fim</dt>
                        <dd className="text-right tabular-nums">
                          {BrazilDate.formatBRNaive(e.dataFim, true)}
                        </dd>
                      </div>
                    )}
                    <div className="flex justify-between gap-2">
                      <dt className="text-neutral-500">Situação</dt>
                      <dd>{statusLabel(e)}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-neutral-500">Endereços</dt>
                      <dd>{e.quantidadeEnderecos}</dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>
          </div>
        )}

        {jaBuscou &&
          !buscando &&
          !erro &&
          resultados.length === 0 &&
          lojaId && (
            <p className="text-center text-sm text-neutral-500">
              Nenhuma entrega encontrada com esse número no período. Tente
              &quot;Últimos 7 dias&quot; se buscou só hoje.
            </p>
          )}
      </div>
    </Modal>
  );
};

export default ModalBuscarPedido;
